import React, { useEffect, useRef, useState } from 'react';
import safeOpenExternalLink from 'window-utils/lib/safeOpenExternalLink';
import sendRenderCompleteMessageToExtension from 'mini-mail-bootstrap/lib/utils/sendRenderCompleteMessageToExtension';
import todoStrings from 'owa-strings/owa-mini-extension-strings';
import { createAppContext } from 'mini-app-context';
import { CustomData, DatapointOptions } from 'owa-analytics-actions';
import { DEFAULT_TASK_FOLDER_ID, TODO_TASKS_RENDERED } from '../constants/todoConstants';
import { dynamicModeChange } from 'mini-theme/lib/utils/dynamicModeChange';
import { enterKeyHandler } from 'window-utils/lib/keyHandlerUtils';
import { formatDate, now, OwaDate } from 'owa-datetime';
import { getCachedTasks } from '../utils/todoCache';
import { getFormattedCurrentDateForTodo } from '../utils/getCurrentDate';
import { getIconClassName } from '@uifabric/styling/lib/index';
import { getOpenInNewWindowIcon } from 'window-utils/lib/getOpenInNewWindowIcon';
import { getTimeFromFetchStart } from 'mini-performance/lib/utils/getTimeFromFetchStart';
import { getTodoModuleUrl } from 'owa-url';
import { Header, HeaderIcon } from 'mini-header';
import { MiniIcons } from 'owa-mini-icons';
import { Module, ModuleSwitcher } from 'mini-module-switcher';
import { Spinner } from 'office-ui-fabric-react';
import { SpinnerSize } from 'fabric-spinner';
import { TaskListSwitcher } from './TaskListSwitcher';
import { TitleJustification } from 'mini-header/lib/components/Header.types';
import { trySetFocusOnElement } from 'dom-utils/lib/utils/focusUtils';
import { useComponentDidMount } from 'mini-hooks';
import { useMiniStrings } from 'mini-hooks/lib/useMiniStrings';
import { when } from 'mobx';

import {
    deleteTask,
    getMyDayTasks,
    getTaskFolders,
    getTasksInTaskList,
    updateTask,
    createTask,
} from '../utils/GraphService';
const todoIllustration = require('../images/tasks.svg');

// TODO: Break into smaller sections
const styles = require('./ToDoModule.scss');
const classNames = require('classnames/bind').bind(styles);

const randomId = (Math.random() * 10000000) >> 0;
const myDayFolderId = `MyDay${randomId}`;

enum StatusText {
    Completed = 'Completed',
    NotStarted = 'NotStarted',
}

enum ImportanceTypes {
    High = 'High',
    Normal = 'Normal',
}

const logger = createAppContext();

const ToDoWrapper = () => {
    useMiniStrings('owa-mini-extension-strings', 'defaultTaskFolderName');

    const [selectedFolderId, setSelectedFolderId] = useState(null);
    const [isTasksLoading, setIsTasksLoading] = useState(true);
    const [tasksMap, setTasksMap] = useState({});
    const [foldersMap, setFoldersMap] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { Completed, NotStarted } = StatusText;
    const { High, Normal } = ImportanceTypes;

    const {
        defaultTaskFolderName,
        addTaskButtonLabel,
        addTaskButtonTitle,
        markAsCompleted,
        markAsNotCompleted,
        markAsImportant,
        removeImportance,
        removeTask,
        completedSectionTitle,
        myDayTitle,
    } = todoStrings;

    const logDatapoint = (
        eventName: string,
        customData?: CustomData,
        options?: DatapointOptions
    ) => {
        const customDataWithParentFolderName = {
            folderName: getSelectedFolderName(),
            ...customData,
        };
        logger.signal(eventName, customDataWithParentFolderName, options);
    };

    const getTodaysTasks = async () => {
        const result = await getMyDayTasks();
        const tasks = result?.Value || [];
        setTasksMap(convertArrayToObject(tasks));
    };

    const getTasksForTheFolder = async (folderId: string) => {
        const responses = await Promise.all([
            getPendingTasksForTheFolder(folderId),
            getRecentlyCompletedTasksForTheFolder(folderId),
        ]);
        let tasks = [];
        responses.forEach(response => {
            tasks = [...tasks, ...(response?.Value || [])];
        });
        setTasksMap(convertArrayToObject(tasks));
    };

    const getPendingTasksForTheFolder = async (folderId: string) =>
        getTasksInTaskList(folderId, {
            $filter: "Status ne 'Completed'",
        });

    const getRecentlyCompletedTasksForTheFolder = async (folderId: string) =>
        getTasksInTaskList(folderId, {
            $filter: "Status eq 'Completed'",
            maxpagesize: 5,
        });

    const fetchTaskFolders = async () => {
        const result = await getTaskFolders();
        const folders = result.Value || [];
        const foldersWithMyDay = [{ Id: myDayFolderId, Name: todoStrings.myDayTitle }, ...folders];
        const formattedFolders = convertArrayToObject(foldersWithMyDay);
        setFoldersMap(formattedFolders);

        if (selectedFolderId) {
            return;
        }
        const defaultFolder: any = getDefaultTaskFolder(formattedFolders);
        setSelectedFolderId(defaultFolder.Id);
    };

    const getDefaultTaskFolder = (foldersMap = {}): any => {
        const folders = getFolders(foldersMap);

        if (folders.length === 0) {
            return { Id: DEFAULT_TASK_FOLDER_ID, Name: defaultTaskFolderName };
        }

        let defaultFolder = folders.find((folder: any) => folder.IsDefaultFolder);

        if (!defaultFolder) {
            defaultFolder = folders.find((folder: any) => folder.Name === defaultTaskFolderName);
        }

        return defaultFolder || folders[0];
    };

    const updateTasksMap = (taskId: string, taskDetails: any) => {
        setTasksMap({ ...tasksMap, [taskId]: taskDetails });
    };

    const addNewTaskToTasksMap = (taskId: string, taskDetails: any) => {
        setTasksMap({ [taskId]: taskDetails, ...tasksMap });
    };

    const removeTaskFromTaskMap = (taskId: string) => {
        const updatedTasks = { ...tasksMap };
        delete updatedTasks[taskId];
        setTasksMap(updatedTasks);
    };

    const onAddNewToDo = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        logDatapoint('ToDoCreateTaskButtonClicked');
        const inputText = newTaskInputRef?.current?.value;

        if (!inputText) {
            requestAnimationFrame(() => trySetFocusOnElement(newTaskInputRef));
            setIsSubmitting(false);
            return;
        }

        let ParentFolderId = selectedFolderId;
        if (selectedFolderId === myDayFolderId) {
            ParentFolderId = getDefaultTaskFolder(foldersMap)?.Id;
        }
        const currentTime: OwaDate = now();

        const request: any = {
            ParentFolderId,
            Subject: inputText,
        };

        if (getSelectedFolderName() === myDayTitle) {
            request.CommittedDay = getFormattedCurrentDateForTodo();
            request.CommittedOrder = currentTime;
        } else {
            request.OrderDateTime = currentTime;
        }

        // Clear the input field value
        if (newTaskInputRef?.current) {
            newTaskInputRef.current.value = '';
        }

        const createdTaskDetails = await createTask(request);
        setIsSubmitting(false);

        addNewTaskToTasksMap(createdTaskDetails?.Id, createdTaskDetails);
        logDatapoint('ToDoNewTaskCreated', { parentFolder: ParentFolderId });
    };

    const onToDoStatusToggle = async (Id: string, ParentFolderId: string, Status: string) => {
        logDatapoint('ToDoToggleStatusButtonClicked');
        const OrderDateTime: OwaDate = now();
        const toStatus = Status === NotStarted ? Completed : NotStarted;

        const updatedTaskDetails = await updateTask({
            Id,
            ParentFolderId,
            Status: toStatus,
            CompletedDateTime: {
                DateTime: formatDate(OrderDateTime, 'yyyy-MM-dd HH:mm:ss'),
                TimeZone: 'Asia/Calcutta',
            },
        });

        updateTasksMap(updatedTaskDetails?.Id, updatedTaskDetails);
        logDatapoint('ToDoTaskStatusToggled', { taskId: Id, fromStatus: Status, toStatus });
    };

    const onToDoImportanceToggle = async (
        Id: string,
        ParentFolderId: string,
        Importance: string
    ) => {
        logDatapoint('ToDoTaskImportanceButtonClicked');
        const OrderDateTime: OwaDate = now();
        const toImportance = Importance === High ? Normal : High;

        const updatedTaskDetails = await updateTask({
            Id,
            ParentFolderId,
            Importance: toImportance,
            OrderDateTime,
        });

        updateTasksMap(updatedTaskDetails?.Id, updatedTaskDetails);

        logDatapoint('ToDoTaskImportanceToggled', {
            taskId: Id,
            fromImportance: Importance,
            toImportance,
        });
    };

    const onRemoveToDo = async (Id: string) => {
        logDatapoint('ToDoTaskDeleteClicked');

        await deleteTask({ Id });
        removeTaskFromTaskMap(Id);

        logDatapoint('ToDoTaskDeleted', { taskId: Id });
    };

    const handleInputKeyPress = ev => {
        if (ev.key === 'Enter' || ev.which === 13) {
            ev.stopPropagation();
            ev.preventDefault();
            onAddNewToDo();
        }
    };

    const onSwitchFolder = async (foldername: string, folderId: string) => {
        logDatapoint('ToDoFolderSwitchButtonClicked', { toFolder: foldername });

        setIsTasksLoading(true);
        setSelectedFolderId(folderId);
        if (folderId === myDayFolderId) {
            await getTodaysTasks();
        } else {
            await getTasksForTheFolder(folderId);
        }
        setIsTasksLoading(false);

        logDatapoint('ToDoFolderSwitchComplete', { folderName: foldername });
    };

    function tasksAreFetched() {
        return getCachedTasks() !== null;
    }

    function areTasksEmpty() {
        return Object.keys(tasksMap).length === 0;
    }

    function initializeTaskState() {
        const tasks = getCachedTasks();
        const taskMap = convertArrayToObject(tasks);
        setTasksMap(taskMap);
        setIsTasksLoading(false);
        requestAnimationFrame(() => {
            const beforePaintTime = getTimeFromFetchStart();
            setTimeout(() => {
                logDatapoint(TODO_TASKS_RENDERED, {
                    afterPaintTime: getTimeFromFetchStart(),
                    beforePaintTime,
                });
            }, 0);
        });

        if (areTasksEmpty()) {
            logDatapoint('ZeroTasks');
        }
    }

    async function triggerTaskFolderFetch() {
        await fetchTaskFolders();
        logDatapoint('ToDoFoldersRendered');
    }

    useEffect(() => {
        logDatapoint('ToDoModuleRendered');
        when(tasksAreFetched, initializeTaskState);
        triggerTaskFolderFetch();
    }, []);

    function getFolders(foldersMapProp?) {
        return Object.values(foldersMapProp || foldersMap);
    }

    function getSelectedFolderName() {
        return foldersMap[selectedFolderId]?.Name || defaultTaskFolderName;
    }

    const newTaskInputRef = React.useRef(null);

    const uncompletedTasks = Object.values(tasksMap).filter(
        (task: any) => task.Status !== Completed
    );
    const completedTasks = Object.values(tasksMap).filter((task: any) => task.Status === Completed);

    const renderToDoList = (list = []) => {
        if (list.length === 0) {
            return null;
        }

        return (
            <div className="listContainer">
                {list.map(({ Status, Subject, Id, Importance, ParentFolderId }, index) => {
                    const isCompleted = Status === Completed;
                    const isImportant = Importance === High;
                    const todoTextClasses = classNames(
                        isCompleted ? styles.complete : '',
                        styles.text,
                        'allowTextSelection'
                    );
                    const checkboxTitle = isCompleted ? markAsNotCompleted : markAsCompleted;

                    return (
                        <div key={index} className={styles.listItem}>
                            <div className={styles.textContainer}>
                                <div
                                    className={styles.round}
                                    tabIndex={0}
                                    onKeyDown={e => {
                                        if (e.key == 'Enter') {
                                            onToDoStatusToggle(Id, ParentFolderId, Status);
                                        }
                                    }}
                                    role={'checkbox'}
                                    aria-label={checkboxTitle}
                                    aria-checked={isCompleted}
                                    title={checkboxTitle}>
                                    <input
                                        type="checkbox"
                                        id={Id}
                                        tabIndex={-1}
                                        checked={isCompleted}
                                        onChange={() =>
                                            onToDoStatusToggle(Id, ParentFolderId, Status)
                                        }
                                    />
                                    <label
                                        htmlFor={Id}
                                        tabIndex={-1}
                                        aria-label={checkboxTitle}
                                        title={checkboxTitle}
                                        onKeyDown={enterKeyHandler(() =>
                                            onToDoStatusToggle(Id, ParentFolderId, Status)
                                        )}></label>
                                </div>
                                <div className={todoTextClasses} tabIndex={0} aria-label={Subject}>
                                    {Subject}
                                </div>
                            </div>
                            <div className={styles.todoActionsContainer}>
                                <div className={styles.star}>
                                    <span
                                        onClick={() =>
                                            onToDoImportanceToggle(Id, ParentFolderId, Importance)
                                        }
                                        onKeyDown={enterKeyHandler(() =>
                                            onToDoImportanceToggle(Id, ParentFolderId, Importance)
                                        )}
                                        role="button"
                                        tabIndex={0}
                                        title={isImportant ? removeImportance : markAsImportant}
                                        className={classNames(
                                            isImportant ? styles.important : undefined,
                                            getIconClassName(
                                                isImportant
                                                    ? MiniIcons.FavoriteStarFill
                                                    : MiniIcons.FavoriteStar
                                            )
                                        )}
                                    />
                                </div>
                                <div className={styles.delete}>
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        title={removeTask}
                                        onClick={() => onRemoveToDo(Id)}
                                        onKeyDown={enterKeyHandler(() => onRemoveToDo(Id))}
                                        className={getIconClassName(MiniIcons.Delete)}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderZeroTasksView = () => (
        <div className={styles.zeroTasksContainer}>
            <img
                src={todoIllustration}
                alt={todoStrings.zeroTasksIllustrationAlt}
                className={styles.todoImage}
            />
            <div className={styles.primaryTodoText}>{todoStrings.addFirstTaskLabel}</div>
        </div>
    );

    const renderAddNewTaskInput = () => (
        <div className={classNames(styles.newTodo, styles.listItem)}>
            <input
                type="text"
                ref={newTaskInputRef}
                placeholder={addTaskButtonTitle}
                className={styles.inputText}
                onKeyPress={handleInputKeyPress}
                required
            />
            <span
                title={addTaskButtonTitle}
                className={classNames(styles.add, getIconClassName(MiniIcons.Add), 'addTaskAction')}
                aria-label={addTaskButtonTitle}
                role="button"
                onMouseDown={onAddNewToDo}
            />
            <span
                title={addTaskButtonTitle}
                aria-label={addTaskButtonTitle}
                role="button"
                className={classNames(styles.addButton, 'addTaskAction', 'addBtn')}
                onMouseDown={onAddNewToDo}>
                {addTaskButtonLabel}
            </span>
        </div>
    );

    const rightIcon: HeaderIcon[] = [
        getOpenInNewWindowIcon({ clickHandler: handleOpenInFullScreenClick }),
    ];

    rightIcon[0].iconRef = useRef(null);

    useEffect(() => {
        trySetFocusOnElement(rightIcon[0].iconRef);
    });

    if (isTasksLoading) {
        return (
            <div className={styles.loaderContainer}>
                <Spinner size={SpinnerSize.large} className={styles.loadingSpinner} />
            </div>
        );
    }

    return (
        <>
            <div className={styles.todoContainer}>
                <TaskListSwitcher
                    folderList={getFolders()}
                    selectedFolderName={getSelectedFolderName()}
                    onSwitchFolder={onSwitchFolder}
                />
                {renderAddNewTaskInput()}
                {areTasksEmpty() && renderZeroTasksView()}
                {renderToDoList(uncompletedTasks)}
                {completedTasks.length === 0 ? null : (
                    <div className={styles.sectionHeading}>{completedSectionTitle}</div>
                )}
                {renderToDoList(completedTasks)}
            </div>
        </>
    );
};

function convertArrayToObject(tasks: any[]) {
    return tasks.reduce((acc, task) => {
        acc[task.Id] = task;
        return acc;
    }, {});
}

function handleOpenInFullScreenClick() {
    safeOpenExternalLink(getTodoModuleUrl());
}

export const ToDoModule = () => {
    useComponentDidMount(() => {
        sendRenderCompleteMessageToExtension();
        dynamicModeChange();
    });

    useMiniStrings('owa-mini-extension-strings', 'defaultTaskFolderName');

    const rightIcon: HeaderIcon[] = [
        getOpenInNewWindowIcon({ clickHandler: handleOpenInFullScreenClick }),
    ];
    rightIcon[0].iconRef = useRef(null);
    useEffect(() => {
        trySetFocusOnElement(rightIcon[0].iconRef);
    });

    return (
        <>
            <div role="heading" aria-level={1} tabIndex={0} aria-label={todoStrings.moduleName}>
                <Header
                    rightIcon={rightIcon}
                    title={todoStrings.moduleName}
                    titleStyles={styles.titleStyle}
                    titleJustification={TitleJustification.LEFT}
                    headerStyles={styles.todoHeaderStyle}
                    showTitle
                    extensionHeaderStyles={{
                        containerStyle: styles.extensionHeader,
                    }}
                />
            </div>
            <ToDoWrapper />
            <ModuleSwitcher currentModule={Module.ToDo} />
        </>
    );
};
